import React, { useEffect, useState } from 'react'

import { Button, Form, Header, Modal, Message } from 'semantic-ui-react'

import Data, { ITheme } from '../../api/Data'
import CustomLabel from '../../components/common/custom-label/CustomLabel'
import ProjectOverlayFields from '../../components/project/ProjectOverlayFields'
import ProjectWorkflowSelector from '../../components/project/ProjectWorkflowSelector'
import { DEFAULT_PROJECT_TYPE, ProjectType } from '../../config/projectTypes'
import {
  DEFAULT_PROJECT_ATTRIBUTION_SETTINGS,
  findProjectThemeSeed,
  ProjectAttributionSettings,
  suggestProjectName,
} from '../../utils/projectSetup'

const ProjectStartProjectModal = ({
  teamName,
  watermarkUrl,
  brandColor,
  setShowModal,
  handleCreateProject,
}: IProps) => {
  const [step, setStep] = useState(1)
  const [projectName, setProjectName] = useState(suggestProjectName(teamName, DEFAULT_PROJECT_TYPE))
  const [projectNameWasEdited, setProjectNameWasEdited] = useState(false)
  const [projectType, setProjectType] = useState<ProjectType>(DEFAULT_PROJECT_TYPE)
  const [projectTheme, setProjectTheme] = useState<ITheme | null>(null)
  const [attributionSettings, setAttributionSettings] = useState<ProjectAttributionSettings>(
    DEFAULT_PROJECT_ATTRIBUTION_SETTINGS
  )
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    const fetchThemes = async () => {
      const { data, error } = await Data.getThemes()

      setLoading(false)
      if (error) return setLoadError(true)

      setProjectTheme(findProjectThemeSeed(data))
    }

    fetchThemes()
  }, [])

  const createProject = async () => {
    if (!projectTheme) return

    setLoading(true)
    await handleCreateProject(projectName.trim(), projectTheme, projectType, attributionSettings)
    setLoading(false)
  }

  const changeProjectType = (newProjectType: ProjectType) => {
    setProjectType(newProjectType)

    if (!projectNameWasEdited) {
      setProjectName(suggestProjectName(teamName, newProjectType))
    }
  }

  return (
    <Modal dimmer="blurring" open size="large" onClose={() => setShowModal(false)}>
      <Modal.Header>Create a project</Modal.Header>

      <Modal.Content>
        {step === 1 ? (
          <>
            <Header as="h2">What do you want to collect?</Header>
            <p>Choose the submission workflow first. We’ll suggest a project name that you can edit.</p>
            <Form>
              <ProjectWorkflowSelector value={projectType} onChange={changeProjectType} />
              <Form.Field>
                <CustomLabel>
                  <CustomLabel.Text text="Suggested project name — you can edit this" />
                  <CustomLabel.WordCount word={projectName} limit={75} />
                </CustomLabel>
                <Form.Input
                  autoFocus
                  maxLength={75}
                  value={projectName}
                  onChange={(event, data) => {
                    setProjectNameWasEdited(true)
                    setProjectName(data.value)
                  }}
                />
              </Form.Field>
            </Form>
          </>
        ) : (
          <>
            <Header as="h2">Set up watermarking and attribution</Header>
            <p>
              Set up how each video you receive from contributors will look, denoting your brand ownership together with
              their contribution.
            </p>
            {loadError && <Message error content="The project layout could not be loaded. Please try again." />}
            {!loadError && !projectTheme && !loading && (
              <Message warning content="A project layout seed could not be loaded. Please try again." />
            )}
            <ProjectOverlayFields
              value={attributionSettings}
              onChange={setAttributionSettings}
              watermarkUrl={watermarkUrl}
              brandColor={brandColor}
            />
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setShowModal(false)}>Close</Button>
        {step === 2 && (
          <Button disabled={loading} onClick={() => setStep(1)}>
            Back
          </Button>
        )}
        {step === 1 ? (
          <Button primary disabled={projectName.trim() === '' || loading || loadError} onClick={() => setStep(2)}>
            Next: watermarking & attribution
          </Button>
        ) : (
          <Button primary loading={loading} disabled={!projectTheme || loading} onClick={createProject}>
            Create project
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  )
}

interface IProps {
  teamName: string
  watermarkUrl?: string
  brandColor?: string
  setShowModal: (arg: boolean) => void
  handleCreateProject: (
    projectName: string,
    theme: ITheme,
    projectType: ProjectType,
    attributionSettings: ProjectAttributionSettings
  ) => Promise<void>
}

export default ProjectStartProjectModal
